import React, { useEffect, useRef, useState } from 'react';

/**
 * Loading state for long people / candidate searches: a small researcher
 * walks to the filing cabinet, pulls a profile, inspects it and files it in
 * one of three trays, on a 7-second loop, with a rotating status line.
 *
 * Searches give no progress data until they finish, so nothing here
 * suggests progress: the loop is indeterminate and the messages depend on
 * elapsed time only. Trays fill up over a long wait and empty again.
 *
 * Use with useSearchLoaderPhase(active): the loader only appears if the
 * search is still running after 400ms (fast searches go straight to their
 * results) and fades out over 200ms when it finishes.
 *
 *   const phase = useSearchLoaderPhase(searching);
 *   {phase !== 'off' && phase !== 'waiting' ? <SearchLoader leaving={phase === 'leaving'} /> : <Results />}
 *
 * Colours come from CSS variables on the wrapper (--sl-*), set to the app's
 * current light palette, so a future dark theme only has to override them.
 */

export type SearchLoaderPhase = 'off' | 'waiting' | 'on' | 'leaving';

const SHOW_AFTER_MS = 400;
const FADE_MS = 200;

export function useSearchLoaderPhase(active: boolean): SearchLoaderPhase {
  const [phase, setPhase] = useState<SearchLoaderPhase>(active ? 'waiting' : 'off');
  useEffect(() => {
    if (active) {
      setPhase((p) => (p === 'on' ? 'on' : 'waiting'));
      const t = window.setTimeout(() => setPhase('on'), SHOW_AFTER_MS);
      return () => window.clearTimeout(t);
    }
    setPhase((p) => (p === 'on' ? 'leaving' : 'off'));
    const t = window.setTimeout(() => setPhase('off'), FADE_MS);
    return () => window.clearTimeout(t);
  }, [active]);
  return phase;
}

const MESSAGES = ['Pulling profiles…', 'Checking who works where…', 'Sorting by employer…', 'Tracking down hiring managers…'];
const LONG_MESSAGES = ['Big market. Still working on it…', 'Nearly through the cabinet…'];
const MESSAGE_MS = 2800;
const LONG_AFTER_MS = 10000;

// Scene geometry (viewBox 0 0 600 210).
const FLOOR = 170;
const CENTRE = 300;
const AT_CABINET = 150;
const TRAYS = [450, 495, 540];
const TRAY_COLOURS = ['#8b5cf6', '#0d9488', '#e07a5f'];
const LOOP_MS = 7000;
const START_COUNTS = [1, 0, 2];
const MAX_STACK = 5;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const span = (p: number, a: number, b: number) => clamp01((p - a) / (b - a));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

// Where everything is at loop position p (0–1) for the given target tray.
function frame(p: number, trayX: number) {
  const standAtTray = trayX - 34;
  let x = CENTRE;
  let dir = 1; // 1 = facing right
  let walking = 0; // 0–1 progress through the current walk (for the legs), -1 = standing
  const walk = (a: number, b: number, from: number, to: number) => {
    const t = span(p, a, b);
    x = lerp(from, to, easeInOut(t));
    dir = to < from ? -1 : 1;
    walking = t;
  };
  if (p < 0.12) walk(0, 0.12, CENTRE, AT_CABINET);
  else if (p < 0.27) { x = AT_CABINET; dir = -1; walking = -1; }
  else if (p < 0.4) walk(0.27, 0.4, AT_CABINET, CENTRE);
  else if (p < 0.6) { x = CENTRE; dir = 1; walking = -1; }
  else if (p < 0.74) walk(0.6, 0.74, CENTRE, standAtTray);
  else if (p < 0.82) { x = standAtTray; dir = 1; walking = -1; }
  else walk(0.82, 1, standAtTray, CENTRE);

  const steps = walking >= 0 ? walking * Math.PI * 2 * 3 : 0;
  const swing = walking >= 0 ? 7 * Math.sin(steps) : 0;
  const bob = walking >= 0 ? -2.5 * Math.abs(Math.sin(steps)) : 0;

  // Top drawer slides out and back while he stands at the cabinet.
  const drawer = p >= 0.12 && p < 0.27 ? 22 * Math.sin(Math.PI * span(p, 0.12, 0.27)) : 0;

  const inspecting = p >= 0.4 && p < 0.6;
  const hand = { x: x + dir * 14, y: FLOOR - 34 + bob };

  // The paper: out of the drawer into his hand, carried, inspected, dropped.
  let paper: { x: number; y: number; show: boolean } = { x: 0, y: 0, show: false };
  if (p >= 0.18 && p < 0.23) {
    const t = easeInOut(span(p, 0.18, 0.23));
    const from = { x: 92 + drawer, y: 82 };
    paper = { x: lerp(from.x, hand.x, t), y: lerp(from.y, hand.y, t) - 26 * Math.sin(Math.PI * t), show: true };
  } else if (p >= 0.23 && p < 0.74) {
    // Inspecting: held out a little further, lens over its middle.
    paper = inspecting ? { x: x + dir * 22, y: hand.y - 8, show: true } : { x: hand.x, y: hand.y, show: true };
  } else if (p >= 0.74 && p < 0.8) {
    const t = easeInOut(span(p, 0.74, 0.8));
    paper = { x: lerp(hand.x, trayX, t), y: lerp(hand.y, FLOOR - 12, t) - 22 * Math.sin(Math.PI * t), show: true };
  }

  const wobble = inspecting ? 8 * Math.sin(span(p, 0.4, 0.6) * Math.PI * 4) : 0;
  const tickT = p >= 0.52 && p < 0.66 ? span(p, 0.52, 0.56) : -1;
  return { x, dir, swing, bob, drawer, inspecting, paper, wobble, tickT };
}

const css = `
.sl-root { --sl-line: #d1d5db; --sl-surface: #ffffff; --sl-drawer: #f9fafb; --sl-handle: #9ca3af;
  --sl-text: #6b7280; --sl-body: #7c6ad6; --sl-arm: #5b4bc4; --sl-cap: #4c3c9a; --sl-head: #e8907a;
  --sl-legs: #4b5563; --sl-ink: #9ca3af; --sl-lens: rgba(139, 92, 246, 0.14); --sl-tick: #16a34a; }
.sl-msg { animation: sl-fade 0.35s ease-out; }
@keyframes sl-fade { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: none; } }
`;

export const SearchLoader: React.FC<{ leaving?: boolean; label?: string }> = ({ leaving = false, label = 'Searching your market' }) => {
  const person = useRef<SVGGElement>(null);
  const legA = useRef<SVGLineElement>(null);
  const legB = useRef<SVGLineElement>(null);
  const arm = useRef<SVGLineElement>(null);
  const glass = useRef<SVGGElement>(null);
  const tick = useRef<SVGGElement>(null);
  const drawer = useRef<SVGGElement>(null);
  const paper = useRef<SVGGElement>(null);

  const [counts, setCounts] = useState<number[]>(START_COUNTS);
  const [msg, setMsg] = useState(MESSAGES[0]);

  // Status line: every 2.8s, the long-wait messages after 10s (elapsed time
  // only — they never claim progress).
  useEffect(() => {
    const started = Date.now();
    let i = 0;
    const iv = window.setInterval(() => {
      i += 1;
      const list = Date.now() - started >= LONG_AFTER_MS ? LONG_MESSAGES : MESSAGES;
      setMsg(list[i % list.length]);
    }, MESSAGE_MS);
    return () => window.clearInterval(iv);
  }, []);

  // The animation: one requestAnimationFrame loop setting SVG attributes —
  // React doesn't re-render per frame. Time comes from a running clock that
  // stops while the tab is hidden, so the loop never jumps or drifts.
  useEffect(() => {
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const set = (el: Element | null, attrs: Record<string, string | number>) => {
      if (!el) return;
      for (const k of Object.keys(attrs)) el.setAttribute(k, String(attrs[k]));
    };
    const draw = (p: number, tray: number) => {
      const f = frame(p, TRAYS[tray]);
      set(person.current, { transform: `translate(${f.x.toFixed(2)} ${(FLOOR + f.bob).toFixed(2)}) scale(${f.dir} 1)` });
      set(legA.current, { x2: (-4 + f.swing).toFixed(2) });
      set(legB.current, { x2: (4 - f.swing).toFixed(2) });
      // Inspecting: hand up on the handle, lens over the profile he holds.
      set(arm.current, f.inspecting ? { x2: 12, y2: -33 } : { x2: 14, y2: -34 });
      set(glass.current, { visibility: f.inspecting ? 'visible' : 'hidden', transform: `translate(15 -36) rotate(${f.wobble.toFixed(2)})` });
      // Pop in with a slight overshoot (ease-out-back).
      const u = clamp01(f.tickT) - 1;
      const s = f.tickT < 0 ? 0 : 1 + 2.7 * u * u * u + 1.7 * u * u;
      set(tick.current, { visibility: f.tickT < 0 ? 'hidden' : 'visible', transform: `translate(0 -88) scale(${s.toFixed(3)})` });
      set(drawer.current, { transform: `translate(${f.drawer.toFixed(2)} 0)` });
      set(paper.current, { visibility: f.paper.show ? 'visible' : 'hidden', transform: `translate(${(f.paper.x - 7).toFixed(2)} ${(f.paper.y - 9).toFixed(2)})` });
    };

    if (reduce) {
      // Still frame: standing in the middle, inspecting a profile.
      draw(0.45, 0);
      set(tick.current, { visibility: 'hidden' });
      return undefined;
    }

    let raf = 0;
    let clock = 0; // ms of visible running time
    let last = performance.now();
    let filedLoop = -1;
    const step = (now: number) => {
      clock += Math.min(now - last, 100); // a long frame gap never jumps the scene
      last = now;
      const loop = Math.floor(clock / LOOP_MS);
      const p = (clock % LOOP_MS) / LOOP_MS;
      const tray = loop % TRAYS.length;
      draw(p, tray);
      // Paper lands at 0.80: one sheet on that tray, once per loop.
      if (p >= 0.8 && filedLoop !== loop) {
        filedLoop = loop;
        setCounts((c) => {
          const next = c.map((n, i) => (i === tray ? n + 1 : n));
          return next.some((n) => n > MAX_STACK) ? [0, 0, 0] : next;
        });
      }
      raf = requestAnimationFrame(step);
    };
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) { last = performance.now(); raf = requestAnimationFrame(step); }
    };
    raf = requestAnimationFrame(step);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className="sl-root bg-white rounded-xl border border-gray-200 px-4 py-8 flex flex-col items-center"
      style={{ opacity: leaving ? 0 : 1, transition: `opacity ${FADE_MS}ms ease-out`, minHeight: 320 }}
    >
      <style>{css}</style>
      <svg viewBox="0 0 600 210" className="w-full" style={{ maxWidth: 600 }} aria-hidden="true" focusable="false">
        <line x1="20" y1={FLOOR} x2="580" y2={FLOOR} stroke="var(--sl-line)" strokeWidth="1.5" />

        {/* Filing cabinet; the top drawer slides out */}
        <rect x="40" y="70" width="74" height="100" rx="4" fill="var(--sl-surface)" stroke="var(--sl-line)" strokeWidth="1.5" />
        {[108, 140].map((y) => (
          <g key={y}>
            <rect x="46" y={y} width="62" height="26" rx="3" fill="var(--sl-drawer)" stroke="var(--sl-line)" strokeWidth="1.2" />
            <rect x="69" y={y + 11} width="16" height="4" rx="2" fill="var(--sl-handle)" />
          </g>
        ))}
        <g ref={drawer}>
          <rect x="46" y="76" width="62" height="26" rx="3" fill="var(--sl-drawer)" stroke="var(--sl-line)" strokeWidth="1.2" />
          <rect x="69" y="87" width="16" height="4" rx="2" fill="var(--sl-handle)" />
        </g>

        {/* Trays and their stacks of filed profiles */}
        {TRAYS.map((cx, i) => (
          <g key={cx}>
            {Array.from({ length: counts[i] }).map((_, k) => (
              <rect key={k} x={cx - 13} y={FLOOR - 5 - k * 3} width="26" height="2" rx="1" fill={TRAY_COLOURS[i]} />
            ))}
            <path d={`M${cx - 18} ${FLOOR - 20} V${FLOOR} H${cx + 18} V${FLOOR - 20}`} fill="none" stroke="var(--sl-line)" strokeWidth="1.5" strokeLinejoin="round" />
          </g>
        ))}

        {/* The profile he carries — drawn first, so his hand and the glass sit over it */}
        <g ref={paper} visibility="hidden">
          <rect width="14" height="18" rx="1.5" fill="var(--sl-surface)" stroke="var(--sl-line)" strokeWidth="1.2" />
          {[5, 9, 13].map((y) => <line key={y} x1="3" y1={y} x2={y === 13 ? 8 : 11} y2={y} stroke="var(--sl-ink)" strokeWidth="1.2" strokeLinecap="round" />)}
        </g>
        {/* The researcher — origin at his feet, facing right */}
        <g ref={person} transform={`translate(${CENTRE} ${FLOOR})`}>
          <line ref={legA} x1="-4" y1="-18" x2="-4" y2="0" stroke="var(--sl-legs)" strokeWidth="4" strokeLinecap="round" />
          <line ref={legB} x1="4" y1="-18" x2="4" y2="0" stroke="var(--sl-legs)" strokeWidth="4" strokeLinecap="round" />
          <rect x="-10" y="-50" width="20" height="32" rx="7" fill="var(--sl-body)" />
          <circle cx="0" cy="-61" r="10" fill="var(--sl-head)" />
          <path d="M-10 -63 A10 10 0 0 1 10 -63 Z" fill="var(--sl-cap)" />
          <circle cx="5" cy="-60" r="1.4" fill="#1f2937" />
          <line ref={arm} x1="3" y1="-42" x2="14" y2="-34" stroke="var(--sl-arm)" strokeWidth="4" strokeLinecap="round" />
          <g ref={glass} visibility="hidden">
            <line x1="-3" y1="5" x2="2" y2="-1" stroke="var(--sl-legs)" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="6" cy="-6" r="6.5" fill="var(--sl-lens)" stroke="var(--sl-legs)" strokeWidth="2" />
          </g>
          <g ref={tick} visibility="hidden">
            <circle r="7" fill="var(--sl-tick)" />
            <path d="M-3.2 0.2 L-0.8 2.6 L3.4 -2.2" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        </g>

      </svg>
      <div key={msg} className="sl-msg mt-2 text-sm text-center" style={{ color: 'var(--sl-text)' }}>{msg}</div>
    </div>
  );
};

export default SearchLoader;
